import {
  useAutoTranslation,
  useContentCacheStore,
  useGetLibraryContentByPage,
  useUserConfigurationsStore,
} from "@/entities"
import { LibraryItem } from "./library-item"
import styles from "./styles.module.scss"
import { cn } from "@/lib/utils"
import { EmptyState } from "@/components/ui/empty-state"
import { Skeleton } from "@/components/ui/skeleton"
import { FileText, Puzzle } from "lucide-react"
import { UiPagination } from "@/shared/ui"
import {
  DISCOVERED_TRANSLATION_KEYS,
  MOBILE_TRANSLATION_KEYS,
} from "@/shared/constants"
import { ContentItemCreationMode } from "@/shared/api/generated"

interface LibraryProps {
  /** "extension" = only items created from a browser-extension selection
   * (creationMode=SELECTION); default = the whole library. */
  filter?: "all" | "extension"
}

export const Library = ({ filter = "all" }: LibraryProps) => {
  const { t } = useAutoTranslation()
  const isExtension = filter === "extension"
  const activeAllPage = useContentCacheStore(s => s.activeAllPage)
  const setActiveAllPage = useContentCacheStore(s => s.setActiveAllPage)
  const activeExtensionPage = useContentCacheStore(s => s.activeExtensionPage)
  const setActiveExtensionPage = useContentCacheStore(
    s => s.setActiveExtensionPage
  )
  const activePage = isExtension ? activeExtensionPage : activeAllPage
  const setActivePage = isExtension ? setActiveExtensionPage : setActiveAllPage
  const viewMode = useUserConfigurationsStore(s => s.viewMode)
  const { data, isLoading, isFetching } = useGetLibraryContentByPage(
    activePage,
    isExtension ? ContentItemCreationMode.SELECTION : undefined
  )

  if (data && data.data.length === 0) {
    if (isExtension) {
      return (
        <EmptyState
          icon={Puzzle}
          title={t(MOBILE_TRANSLATION_KEYS.NO_EXTENSION_CONTENT_YET)}
          description={t(
            MOBILE_TRANSLATION_KEYS.EXTENSION_CONTENT_EMPTY_DESCRIPTION
          )}
        />
      )
    }
    return (
      <EmptyState
        icon={FileText}
        title={t(DISCOVERED_TRANSLATION_KEYS.NO_CONTENT_YET)}
        description={t(
          DISCOVERED_TRANSLATION_KEYS.UPLOAD_ADOCUMENT_PASTE_TEXT_OR_ADD_AWEB_ARTICLE_TO_GET_STARTED_WITH_AUDIO_CONVERSION
        )}
      />
    )
  }

  return (
    <>
      <div
        className={cn({
          [styles.gridWrapper]: viewMode === "grid",
          [styles.listWrapper]: viewMode === "list",
        })}
      >
        {!isLoading &&
          !isFetching &&
          data &&
          data.data.map((item, index) => {
            return (
              <LibraryItem
                key={`library-item-${item.id}`}
                {...item}
                libraryViewMode={viewMode}
                isFirstIdx={index === 0}
                isLastIdx={index === data.data.length - 1}
              />
            )
          })}

        {(isLoading || isFetching || !data) &&
          Array.from({ length: 8 }).map((_, i) => {
            if (viewMode === "grid") {
              return (
                <Skeleton
                  className="h-[340px] rounded-[16px]"
                  key={`skeleton-library-${i}-grid`}
                />
              )
            }
            return (
              <Skeleton
                className={cn("border-border h-[74px] rounded-none border-b", {
                  "rounded-t-[12px]": i === 0,
                  "rounded-b-[12px]": i === 7,
                })}
                key={`skeleton-library-${i}-list`}
              />
            )
          })}
      </div>
      {data && (
        <div className="mt-6 flex justify-center">
          <UiPagination
            currentPage={activePage}
            totalPages={data.meta.totalPages}
            onPageChange={setActivePage}
          />
        </div>
      )}
      <div className="h-6"></div>
    </>
  )
}
